import * as d3 from 'd3'
import * as dc from 'dc'
import { MATURITY_LABELS } from '@/utils/flowSightBuckets'
import { BUY_COLOR, SELL_COLOR } from '@/utils/flowSightColors'
import { conversionRateOf, observationsOf, emptyConversionValue } from '../calculations/conversionRate.js'


export function createConversionRateByTenorChart ({ el, chartGroup, width, height, dims, grp, t, drilldown }) {
  const TENOR_COUNT = MATURITY_LABELS.length
  const GAP_SLOTS = 1
  const OUTER_PADDING = 0.5

  const domainIdxKeys = [] // apenas bins reais (BUY/SELL)
  const domainSlots = [] // bins reais + gaps para geometria no eixo
  const tickKeys = [] // ticks em BUY; com centerBar, ficam no encontro BUY/SELL
  for (let i = 0; i < TENOR_COUNT; i++) {
    const buyKey = `${i}|BUY`
    const sellKey = `${i}|SELL`
    domainIdxKeys.push(buyKey, sellKey)
    domainSlots.push(buyKey, sellKey)
    tickKeys.push(buyKey)
    if (i < (TENOR_COUNT - 1)) {
      for (let g = 0; g < GAP_SLOTS; g++) domainSlots.push(`__GAP__${i}_${g}`)
    }
  }

  // Deterministic Y-axis policy: domain [0..max] with data, fixed [0..1]
  // (no ticks) when empty - avoids the 0% baseline visually "jumping" to
  // the vertical center when filtered data is empty.
  const applyYAxisPolicy = (chart) => {
    const rows = chart?.group?.().all?.() || []
    const values = rows
      .map((d) => conversionRateOf(d?.value))
      .filter((v) => Number.isFinite(v))
    const maxRate = d3.max(values) || 0

    if (!(maxRate > 0)) {
      chart.y(d3.scaleLinear().domain([0, 1]))
      chart.yAxis().tickValues([])
      return
    }
    chart.y(d3.scaleLinear().domain([0, maxRate]))
    chart.yAxis().tickValues(null)
  }

  // The "i|SIDE" -> {cnt,vol,volDeal} lookup Map is rebuilt fresh inside .all()
  // every render/redraw (not cached once at construction time), since dc
  // calls chart.group().all() on every cycle expecting the CURRENT
  // crossfilter state - a stale cached Map would leave this chart's own
  // data disconnected from what every sibling chart is seeing.
  const safeValue = (mapVal, k) => {
    const v = mapVal.get(k)
    return (v && typeof v.vol === 'number' && typeof v.volDeal === 'number') ? v : emptyConversionValue()
  }
  const grpOrdered = {
    all: () => {
      const mapVal = new Map(grp.grpConvIdx.all().map((d) => [String(d.key), d.value]))
      return domainIdxKeys.map((k) => ({ key: k, value: safeValue(mapVal, k) }))
    },
  }

  const bySide = (k) => (String(k).endsWith('|BUY') ? 'BUY' : 'SELL')
  const tenorOf = (k) => {
    const [iStr] = String(k).split('|')
    return MATURITY_LABELS[parseInt(iStr, 10)] || ''
  }

  const chart = dc.barChart(el, chartGroup)
  chart
    .width(width)
    .height(height + 40)
    .margins({ top: 10, right: 20, bottom: 42, left: 56 })
    .dimension(dims.dimTenorIdxSide)
    .controlsUseVisibility(true)
    .group(grpOrdered)
    .ordering(() => 0)
    .keyAccessor((d) => String(d.key))
    .x(d3.scaleBand().domain(domainSlots))
    .xUnits(dc.units.ordinal)
    .elasticY(true)
    .renderHorizontalGridLines(true)
    .centerBar(true)
    .barPadding(0)
    .outerPadding(OUTER_PADDING)
    .brushOn(false)
    .valueAccessor((d) => conversionRateOf(d.value))
    // Labels are hand-drawn in drawBarValues below, not via .renderLabel(): dc
    // puts its own at `y(top of bar) - padding`, i.e. always OUTSIDE, and the
    // position is written by a transition that would overwrite anything we set
    // afterwards.
    .colorAccessor((d) => bySide(d.key))
    .ordinalColors([BUY_COLOR, SELL_COLOR])
    .title((d) => {
      const v = conversionRateOf(d.value)
      return `${tenorOf(d.key)} • ${bySide(d.key)}\n${t('flowSightConversions.conversion')}: ${(v * 100).toFixed(1)}%`
    })

  chart.xAxis().tickValues(tickKeys).tickFormat((k) => MATURITY_LABELS[parseInt(String(k).split('|')[0], 10)] || '')
  chart.yAxis().ticks(6).tickFormat(d3.format('.0%'))

  chart.filterHandler((dimTenorIdxSide, filters) => {
    drilldown.replace('conversionRate', filters, { clear: ['bubble'] })
    return filters
  })

  // chart.onClick = (datum) => {
  //   chart.filter(chart.keyAccessor()(datum))
  // }

  // Value inside the bar, falling back to just above it when the bar is too
  // short to hold the text (a 0.28% bar is ~3px tall). Reads the geometry off
  // the rendered rects rather than recomputing dc's bar maths.
  const MIN_INSIDE_PX = 20
  const drawBarValues = (c) => {
    const body = c.select('g.chart-body')
    if (body.empty()) return
    body.selectAll('text.bar-value').remove()

    // Positions come from the SCALES, not from the rendered rects: dc animates
    // a bar's x/y/width/height, so at this point those attributes still hold
    // the pre-animation values (height 0 on a first render). Reading them back
    // pinned every label to the axis line.
    const y0 = c.y()(0)

    c.selectAll('rect.bar').each(function (d) {
      const value = d?.data?.value
      if (!observationsOf(value)) return

      const rate = conversionRateOf(value)
      const yTop = c.y()(rate)
      const barHeight = Math.abs(y0 - yTop)
      // centerBar(true) makes x(key) the bar's CENTRE - the same x dc uses for
      // its own labels.
      const cx = c.x()(d.x)
      if (!Number.isFinite(cx) || !Number.isFinite(yTop)) return

      const inside = barHeight >= MIN_INSIDE_PX

      body.append('text')
        .attr('class', 'bar-value')
        .attr('text-anchor', 'middle')
        .attr('x', cx)
        .attr('y', inside ? yTop + 14 : yTop - 5)
        // !important: Conversions.vue paints every .dc-chart text with
        // var(--muted) !important, which a plain inline fill loses to.
        .attr('style', 'font-size:11px; font-weight:600; pointer-events:none; fill:#fff !important')
        .text(`${(rate * 100).toFixed(0)}%`)
    })
  }

  chart.on('pretransition.bar-values', drawBarValues)
  chart.on('preRender.empty-y', applyYAxisPolicy)
  chart.on('preRedraw.empty-y', applyYAxisPolicy)
  const applyFeedback = (c) => drilldown.decorate(c.selectAll('rect.bar'), (d) => d.x, ['fill'])
  chart.on('postRender.drilldown-feedback', applyFeedback)
  chart.on('postRedraw.drilldown-feedback', applyFeedback)

  return chart
}
